import { useCallback, useEffect } from 'react';
import type { Dispatch, MutableRefObject, RefObject, SetStateAction } from 'react';
import { getSttService } from '../../../services/stt';
import { getTtsService } from '../../../services/tts';
import { store } from '../../../state/store';
import { resolveSpeechLocale } from '../../../utils/locale';
import { getOrchestratorRef } from '../../messageList/MessageList.helpers';
import {
  activeStreamingMsgId,
  cleanForDrivingDisplay,
  cleanupDrivingMode,
  failedTtsMsgIds,
  getBestTranscript,
  hasSendableSpeech,
  lastSpokenMsgId,
  manualStopRequested,
  sendTranscript,
  setActiveStreamingMsgId,
  setLastSpokenMsgId,
  setManualStopRequested,
  setSendAfterStopRequested,
  setStreamedResponseText,
  startListening,
  stopListeningWithoutSending,
  suppressedTtsMsgIds,
  suppressLatestAssistantTts,
  clearNoSpeechTimer,
  clearSilenceTimer,
} from './DrivingMode.helpers';
import { resolveDrivingAssistantText } from '../DrivingMode.shared';

interface UseDrivingModePlaybackArgs {
  transcriptRef: RefObject<HTMLDivElement | null>;
  chatAreaRef: RefObject<HTMLDivElement | null>;
  speechDetectedRef: MutableRefObject<boolean>;
  forceUpdate: Dispatch<SetStateAction<number>>;
  speechRate: number;
}

export function useDrivingModePlayback({
  transcriptRef,
  chatAreaRef,
  speechDetectedRef,
  forceUpdate,
  speechRate,
}: UseDrivingModePlaybackArgs) {
  useEffect(() => {
    if (lastSpokenMsgId === null) {
      const msgs = store.getState().messages;
      if (msgs.length > 0) setLastSpokenMsgId(msgs[msgs.length - 1].id);
    }

    const tts = getTtsService();
    const initialState = store.getState();
    let prevIsLoading = initialState.isLoading;
    let prevToolCallSignature = initialState.toolCalls
      .map((tc) => `${tc.id}:${tc.status}:${tc.error || ''}`)
      .join('|');
    let prevMessagesRef = initialState.messages;
    let prevLastResponse = initialState.drivingMode.lastResponse;
    let prevProcessing = initialState.drivingMode.processing;
    let prevPendingConfirmation = initialState.pendingConfirmation;
    let prevLanguage = initialState.config.language;
    let prevLoggedMsgKey = '';
    const locale = (text: string) => resolveSpeechLocale(text);
    let trackedStreamingMessageId: string | null = null;

    const getDrivingSpeechText = (text: string) => cleanForDrivingDisplay(text);

    const unsubscribe = store.subscribe(() => {
      const state = store.getState();
      if (!state.drivingMode.active) return;

      const toolCallSignature = state.toolCalls
        .map((tc) => `${tc.id}:${tc.status}:${tc.error || ''}`)
        .join('|');
      const relevantStateChanged =
        state.messages !== prevMessagesRef
        || state.drivingMode.lastResponse !== prevLastResponse
        || state.drivingMode.processing !== prevProcessing
        || state.pendingConfirmation !== prevPendingConfirmation
        || state.config.language !== prevLanguage
        || state.isLoading !== prevIsLoading
        || toolCallSignature !== prevToolCallSignature;

      if (!relevantStateChanged) return;

      prevMessagesRef = state.messages;
      prevLastResponse = state.drivingMode.lastResponse;
      prevProcessing = state.drivingMode.processing;
      prevPendingConfirmation = state.pendingConfirmation;
      prevLanguage = state.config.language;

      const wasLoading = prevIsLoading;
      prevIsLoading = state.isLoading;
      if (wasLoading && !state.isLoading && state.drivingMode.processing) {
        store.setDrivingMode({ processing: false });
      }

      const messages = state.messages;
      const streamingMsg = [...messages].reverse().find(
        (message) => message.role === 'assistant' && message.isStreaming,
      );

      if (streamingMsg) {
        const nextText = state.drivingMode.lastResponse || '';
        const spokenNextText = getDrivingSpeechText(nextText);

        if (trackedStreamingMessageId !== streamingMsg.id) {
          trackedStreamingMessageId = streamingMsg.id;
          setActiveStreamingMsgId(null);
          setStreamedResponseText('');
          forceUpdate((count) => count + 1);
          return;
        }

        const currentTextEl = chatAreaRef.current?.querySelector('.driving-msg-text-current');
        if (currentTextEl && spokenNextText) {
          currentTextEl.textContent = spokenNextText;
        }
      }

      const lastMsg = messages[messages.length - 1];
      const resolvedLastMsgText = resolveDrivingAssistantText(
        lastMsg,
        lastMsg?.role === 'assistant' ? lastMsg.id : null,
        state.drivingMode.lastResponse,
      );
      const logKey = lastMsg
        ? `${lastMsg.id}:${resolvedLastMsgText}:${Boolean(lastMsg.isStreaming)}`
        : '';
      if (logKey !== prevLoggedMsgKey) {
        prevLoggedMsgKey = logKey;
      }

      const toolCallsChanged = toolCallSignature !== prevToolCallSignature;
      prevToolCallSignature = toolCallSignature;

      if (state.isLoading && lastMsg?.isStreaming && !toolCallsChanged) return;

      if (
        !lastMsg
        || lastMsg.role !== 'assistant'
        || (!lastMsg.content && !resolvedLastMsgText)
      ) {
        forceUpdate((count) => count + 1);
        return;
      }

      if (lastMsg.isGreeting) {
        setLastSpokenMsgId(lastMsg.id);
        forceUpdate((count) => count + 1);
        return;
      }

      if (lastMsg.isStreaming) {
        forceUpdate((count) => count + 1);
        return;
      }

      if (trackedStreamingMessageId && lastMsg.id === trackedStreamingMessageId) {
        trackedStreamingMessageId = null;
        setActiveStreamingMsgId(null);
        setStreamedResponseText('');
        setLastSpokenMsgId(lastMsg.id);
        if (state.drivingMode.processing) {
          store.setDrivingMode({ processing: false });
        }

        if (suppressedTtsMsgIds.has(lastMsg.id) || failedTtsMsgIds.has(lastMsg.id)) {
          forceUpdate((count) => count + 1);
          return;
        }

        const spokenFinalText = getDrivingSpeechText(state.drivingMode.lastResponse || '');
        if (!spokenFinalText) {
          forceUpdate((count) => count + 1);
          return;
        }

        const speechLocale = locale(spokenFinalText);
        console.debug(`[driving-mode] Final TTS for streamed msg ${lastMsg.id}, locale=${speechLocale}`);
        tts.stop();
        tts.speak(spokenFinalText, speechLocale, {
          onStart: () => store.setDrivingMode({ speaking: true }),
          onEnd: () => {
            store.setDrivingMode({ speaking: false });
            if (
              store.getState().drivingMode.active
              && !store.getState().drivingMode.listening
              && !manualStopRequested
            ) {
              startListening(speechDetectedRef, transcriptRef, forceUpdate);
            }
          },
          onError: () => {
            failedTtsMsgIds.add(lastMsg.id);
            store.setDrivingMode({ speaking: false });
          },
        });
        forceUpdate((count) => count + 1);
        return;
      }

      if (lastMsg.id === lastSpokenMsgId) {
        if (
          wasLoading
          && !state.isLoading
          && !state.drivingMode.listening
          && !state.drivingMode.speaking
          && !state.drivingMode.processing
          && !state.pendingConfirmation
          && !manualStopRequested
          && !tts.isSpeaking()
        ) {
          startListening(speechDetectedRef, transcriptRef, forceUpdate);
          return;
        }
        forceUpdate((count) => count + 1);
        return;
      }

      setActiveStreamingMsgId(null);
      setStreamedResponseText('');
      setLastSpokenMsgId(lastMsg.id);
      const spokenText = cleanForDrivingDisplay(state.drivingMode.lastResponse || '');
      if (state.drivingMode.processing) {
        store.setDrivingMode({ processing: false });
      }
      if (suppressedTtsMsgIds.has(lastMsg.id) || failedTtsMsgIds.has(lastMsg.id)) {
        forceUpdate((count) => count + 1);
        return;
      }

      if (!spokenText) {
        forceUpdate((count) => count + 1);
        return;
      }

      const speechLocale = locale(spokenText);
      console.debug(`[driving-mode] TTS for msg ${lastMsg.id}, locale=${speechLocale}`);
      tts.stop();
      tts.speak(spokenText, speechLocale, {
        onStart: () => store.setDrivingMode({ speaking: true }),
        onEnd: () => {
          store.setDrivingMode({ speaking: false });
          if (
            store.getState().drivingMode.active
            && !store.getState().drivingMode.listening
            && !manualStopRequested
          ) {
            startListening(speechDetectedRef, transcriptRef, forceUpdate);
          }
        },
        onError: () => {
          failedTtsMsgIds.add(lastMsg.id);
          store.setDrivingMode({ speaking: false });
        },
      });
      forceUpdate((count) => count + 1);
    });

    return () => {
      unsubscribe();
      cleanupDrivingMode();
    };
  }, [chatAreaRef, forceUpdate, speechDetectedRef, transcriptRef]);

  useEffect(() => {
    getTtsService().setPlaybackRate(speechRate || 1);
  }, [speechRate]);

  const handleRepeat = useCallback(() => {
    const state = store.getState();
    const lastResponse = state.drivingMode.lastResponse;
    if (!lastResponse) return;

    const tts = getTtsService();
    tts.stop();

    const locale = resolveSpeechLocale(lastResponse, state.config.language);
    store.setDrivingMode({ speaking: true, listening: false, processing: false });
    tts.speak(cleanForDrivingDisplay(lastResponse), locale, {
      onStart: () => store.setDrivingMode({ speaking: true }),
      onEnd: () => {
        store.setDrivingMode({ speaking: false });
        if (store.getState().drivingMode.active && !manualStopRequested) {
          startListening(speechDetectedRef, transcriptRef, forceUpdate);
        }
      },
      onError: () => store.setDrivingMode({ speaking: false }),
    });
  }, [forceUpdate, speechDetectedRef, transcriptRef]);

  const handleSendClick = useCallback((): void => {
    const drivingMode = store.getState().drivingMode;
    if (!drivingMode.listening) return;
    if (!hasSendableSpeech(speechDetectedRef)) {
      stopListeningWithoutSending(speechDetectedRef, transcriptRef);
      return;
    }

    clearSilenceTimer();
    clearNoSpeechTimer();
    setManualStopRequested(false);
    setSendAfterStopRequested(true);
    getSttService().stop();
    store.setDrivingMode({ listening: false, processing: true, error: null });
  }, [speechDetectedRef, transcriptRef]);

  const handleMicClick = useCallback((): void => {
    const drivingMode = store.getState().drivingMode;
    const tts = getTtsService();
    const hasActiveDrivingPlayback = drivingMode.speaking
      || drivingMode.processing
      || tts.isSpeaking()
      || tts.hasActiveStream()
      || !!activeStreamingMsgId;

    if (drivingMode.listening) {
      clearSilenceTimer();
      clearNoSpeechTimer();
      if (getBestTranscript()) {
        void sendTranscript(speechDetectedRef, transcriptRef);
      } else if (speechDetectedRef.current) {
        setManualStopRequested(false);
        setSendAfterStopRequested(true);
        getSttService().stop();
        store.setDrivingMode({ listening: false, processing: true, transcript: '' });
      } else {
        stopListeningWithoutSending(speechDetectedRef, transcriptRef);
      }
      return;
    }

    if (hasActiveDrivingPlayback) {
      setManualStopRequested(true);
      suppressLatestAssistantTts();
      tts.stop();
      setActiveStreamingMsgId(null);
      setStreamedResponseText('');
      getOrchestratorRef()?.interruptCurrentTurn();
      store.setDrivingMode({ speaking: false, processing: false, error: null });
      return;
    }

    startListening(speechDetectedRef, transcriptRef, forceUpdate);
  }, [forceUpdate, speechDetectedRef, transcriptRef]);

  return { handleMicClick, handleRepeat, handleSendClick };
}
